import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Input } from '@multaqa/ui/components/ui/input'
import { toast } from '@multaqa/ui/components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { CheckIcon, PencilIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { formatErrorMessage } from '@/lib/error-message'
import type { GroupEntitlementRow } from './api'
import { useDeleteEntitlement, useUpsertEntitlement } from './api'

/**
 * The non-switch keys — `users.max`, `companies.max`, `storage.mb` — with
 * their resolved value and its source. Editing keeps the edit/save/cancel
 * workflow the flat table used before plans/58 D-17 split it in two; a
 * numeric override is not the "disables a live customer's module" risk F2-6
 * guards against, so clearing one needs no confirmation.
 */
export function GroupLimits({ groupId, rows }: { groupId: string; rows: GroupEntitlementRow[] }) {
  const { t } = useTranslation()
  const columns = [
    t('groups.detail.limits.columns.key'),
    t('groups.detail.limits.columns.value'),
    t('groups.detail.limits.columns.source'),
    t('action.actions'),
  ]

  return (
    <section aria-labelledby="group-limits-heading" data-testid="group-limits-panel">
      <h2 id="group-limits-heading" className="text-h3 mb-3">
        {t('groups.detail.limits.title')}
      </h2>
      {rows.length === 0 ? (
        <p className="text-small text-muted-foreground">{t('groups.detail.limits.empty')}</p>
      ) : (
        <Table className="border border-border" data-testid="group-limits-table">
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              {columns.map((column, index) => (
                <TableHead
                  key={column}
                  className={index === columns.length - 1 ? 'text-end' : undefined}
                >
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <LimitRow key={row.key} groupId={groupId} row={row} />
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}

function LimitRow({ groupId, row }: { groupId: string; row: GroupEntitlementRow }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draftValue, setDraftValue] = useState('')
  const upsert = useUpsertEntitlement()
  const remove = useDeleteEntitlement()
  const saving = upsert.isPending || remove.isPending
  const overridden = row.override_value !== null

  function startEditing() {
    setDraftValue(row.override_value ?? '')
    setEditing(true)
  }

  function cancelEditing() {
    setEditing(false)
    setDraftValue('')
  }

  function save() {
    if (draftValue.trim() === '') return
    upsert.mutate(
      { groupId, key: row.key, value: draftValue.trim() },
      {
        onSuccess: () => {
          setEditing(false)
          setDraftValue('')
        },
        onError: (error) => toast.error(formatErrorMessage(error)),
      },
    )
  }

  function clear() {
    remove.mutate(
      { groupId, key: row.key },
      {
        onSuccess: () => {
          setEditing(false)
          setDraftValue('')
        },
        onError: (error) => toast.error(formatErrorMessage(error)),
      },
    )
  }

  return (
    <TableRow data-testid={`group-limit-row-${row.key}`}>
      <TableCell>
        <span className="text-identifier font-mono">{row.key}</span>
      </TableCell>
      <TableCell className="text-end font-mono tabular-nums">
        {editing ? (
          <Input
            type="number"
            min="0"
            numeric
            value={draftValue}
            onChange={(event) => setDraftValue(event.target.value)}
            disabled={saving}
            autoFocus
            aria-label={`${t('groups.detail.limits.columns.value')} ${row.key}`}
            data-testid={`group-limit-value-${row.key}`}
          />
        ) : (
          (row.effective_value ?? '—')
        )}
      </TableCell>
      <TableCell data-testid={`group-limit-source-${row.key}`}>
        {overridden
          ? t('groups.detail.entitlementsSource.override')
          : t('groups.detail.entitlementsSource.plan')}
      </TableCell>
      <TableCell className="text-end">
        {editing ? (
          <div className="flex items-center justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              loading={upsert.isPending}
              disabled={saving || draftValue.trim() === ''}
              onClick={save}
              aria-label={`${t('action.save')} ${row.key}`}
              data-testid={`group-limit-save-${row.key}`}
            >
              <CheckIcon className="size-3.5" aria-hidden="true" />
            </Button>
            {overridden ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                loading={remove.isPending}
                disabled={saving}
                onClick={clear}
                aria-label={`${t('action.remove')} ${row.key}`}
                data-testid={`group-limit-clear-${row.key}`}
              >
                <XIcon className="size-3.5" aria-hidden="true" />
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={cancelEditing}
              aria-label={`${t('action.cancel')} ${row.key}`}
              data-testid={`group-limit-cancel-${row.key}`}
            >
              {t('action.cancel')}
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={startEditing}
            aria-label={`${t('action.edit')} ${row.key}`}
            data-testid={`group-limit-edit-${row.key}`}
          >
            <PencilIcon className="size-3.5" aria-hidden="true" />
            {t('action.edit')}
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}
